import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  PatchAdminV1StoresStoreIdMarketsIdBody,
  PostAdminV1StoresStoreIdMarketsBody,
} from '@nucleus-commerce/api-client/admin';

import { marketsApi } from '../api/markets-api';

import { marketKeys } from './market-keys';

/** Every change refetches the list: the backend decides ordering and which market is the default. */
export function useCreateMarket(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdMarketsBody) => marketsApi.create(storeId, body),
    meta: { successMessage: 'markets:toasts.created', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketKeys.all(storeId) }),
  });
}

export function useUpdateMarket(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdMarketsIdBody }) =>
      marketsApi.update(storeId, id, body),
    meta: { successMessage: 'markets:toasts.updated', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketKeys.all(storeId) }),
  });
}

/** 409 on the default market is explained by the dialog. */
export function useDeleteMarket(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => marketsApi.remove(storeId, id),
    meta: { successMessage: 'markets:toasts.deleted', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketKeys.all(storeId) }),
  });
}
